import React from 'react';
import { Box, ClickAwayListener, Theme, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Block, CheckCircleOutline, DeleteOutline, Edit, ExpandMore, History } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

export type AchievementAction = 'edit' | 'backfill' | 'toggle' | 'delete';

interface Props {
  isEnabled: boolean;
  canBackfill: boolean;
  onAction: (action: AchievementAction) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      display: 'flex',
      padding: '0.5rem',
      borderRadius: '4px',
      cursor: 'pointer',
      alignItems: 'center',
      '&:hover': { opacity: 0.75 },
    },
    menuItemText: { marginLeft: '0.75rem', fontSize: '14px' },
    tooltip: {
      borderRadius: '12px',
      padding: '0.5rem 1rem',
      backgroundColor: theme.color.tooltip.background,
    },
    tooltipArrow: {
      '&::before': { backgroundColor: theme.color.tooltip.background },
    },
  })
);

export const AchievementItemMenuButton = ({ isEnabled, canBackfill, onAction }: Props) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const items: { action: AchievementAction; label: string; icon: React.ReactNode }[] = [
    { action: 'edit', label: 'Edit', icon: <Edit fontSize='small' /> },
    ...(canBackfill
      ? [{ action: 'backfill' as AchievementAction, label: 'Backfill awards', icon: <History fontSize='small' /> }]
      : []),
    {
      action: 'toggle',
      label: isEnabled ? 'Disable' : 'Enable',
      icon: isEnabled ? <Block fontSize='small' /> : <CheckCircleOutline fontSize='small' />,
    },
    { action: 'delete', label: 'Delete', icon: <DeleteOutline fontSize='small' /> },
  ];

  return (
    <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
      <Tooltip
        arrow
        interactive
        title={
          <Box>
            {items.map((item) => (
              <Box
                key={item.action}
                className={classes.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  onAction(item.action);
                }}
              >
                {item.icon}
                <Typography className={classes.menuItemText}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        }
        disableFocusListener
        disableHoverListener
        disableTouchListener
        placement='bottom-end'
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
      >
        <TabletButton
          size='small'
          variant='outlined'
          color='primary'
          aria-haspopup='true'
          onClick={() => setMenuOpen(true)}
          endIcon={<ExpandMore />}
          style={{
            border: '1px solid #9e9e9e',
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '2px 7px',
            fontSize: '12px',
            color: theme.color.grey.neutral300,
          }}
        >
          Options
        </TabletButton>
      </Tooltip>
    </ClickAwayListener>
  );
};
